import * as React from "react";
import { cn } from "../../lib/utils";
import { Tooltip } from "../Tooltip";
import { Link } from "../Link";
import { Icon } from "../../lib";

export interface FormHeaderProps {
  /**
   * The label text to display
   */
  label: string;
  /**
   * Size variant that affects text size, icon size, and spacing
   */
  size?: "small" | "medium" | "large";
  /**
   * Whether to show "(optional)" text after the label
   */
  isOptional?: boolean;
  /**
   * Whether to show "*" (required indicator) after the label
   */
  isRequired?: boolean;
  /**
   * Tooltip heading text
   */
  infoHeading?: string;
  /**
   * Tooltip description text - if provided, shows the info icon
   */
  infoDescription?: string;
  /**
   * Custom Link component to render on the right side
   * If provided, this will be rendered instead of the default Link with linkText
   */
  LinkComponent?: React.ReactNode;
  /**
   * Link text to display on the right side
   */
  linkText?: string;
  /**
   * Link href or onClick handler
   */
  linkHref?: string;
  /**
   * Link click handler
   */
  onLinkClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  /**
   * Leading icon for the link
   */
  linkLeadingIcon?: React.ReactNode;
  /**
   * Trailing icon for the link
   */
  linkTrailingIcon?: React.ReactNode;
  /**
   * HTML for attribute to associate with form input
   */
  htmlFor?: string;
  /**
   * Custom class name for the container
   */
  className?: string;
  /**
   * Custom class name for the label
   */
  labelClassName?: string;
  /**
   * Custom class name for the link
   */
  linkClassName?: string;
}

const FormHeader = React.forwardRef<HTMLDivElement, FormHeaderProps>(
  (
    {
      label,
      size = "medium",
      isOptional = false,
      isRequired = false,
      infoHeading,
      infoDescription,
      LinkComponent,
      linkText,
      linkHref,
      onLinkClick,
      linkLeadingIcon,
      linkTrailingIcon,
      htmlFor,
      className,
      labelClassName,
      linkClassName,
    },
    ref
  ) => {
    // Size-based configurations
    const sizeConfig = {
      small: {
        textClassName: "text-body-xsmall-semibold",
        textClassNameRegular: "text-caption-small-regular",
        iconSize: 12,
        gap: "gap-1.5",
      },
      medium: {
        textClassName: "text-body-small-semibold",
        textClassNameRegular: "text-caption-medium-regular",
        iconSize: 14,
        gap: "gap-2",
      },
      large: {
        textClassName: "text-body-medium-semibold",
        textClassNameRegular: "text-caption-large-regular",
        iconSize: 16,
        gap: "gap-2.5",
      },
    };

    const config = sizeConfig[size];

    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center justify-between px-1",
          config.gap,
          className
        )}
      >
        <div className={cn("flex items-center", config.gap)}>
          <label
            htmlFor={htmlFor}
            className={cn("flex items-center", labelClassName)}
          >
            <span
              className={cn(
                config.textClassName,
                "text-surface-neutral-subtle"
              )}
            >
              {label}
            </span>
            {isRequired && (
              <span
                className={cn(
                  config.textClassName,
                  "text-color-negative ml-0.5"
                )}
              >
                *
              </span>
            )}
            {isOptional && (
              <span
                className={cn(
                  config.textClassNameRegular,
                  "text-surface-ink-neutral-muted ml-1"
                )}
              >
                (optional)
              </span>
            )}
          </label>
          {infoDescription && (
            <Tooltip description={infoDescription} heading={infoHeading}>
              <Icon name="info" size={config.iconSize} />
            </Tooltip>
          )}
        </div>

        {LinkComponent
          ? LinkComponent
          : linkText && (
              <Link
                href={linkHref}
                onClick={onLinkClick}
                type="action"
                color="primary"
                size={size === "large" ? "small" : "xsmall"}
                leadingIcon={linkLeadingIcon}
                trailingIcon={linkTrailingIcon}
              >
                {linkText}
              </Link>
            )}
      </div>
    );
  }
);

FormHeader.displayName = "FormHeader";

export { FormHeader };
